import type { ImgHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> & {
  alt?: string;
};

const DEFAULT_LOGO = { src: '/Logo_Dual.svg', width: 147, height: 32 };

// Per-language wordmark overrides. Korean shows the co-branded Clearlab x
// Migwang mark; every other language falls back to DEFAULT_LOGO.
const LOGO_BY_LANGUAGE: Record<string, { src: string; width: number; height: number }> = {
  ko: { src: '/ClearlabXMigwang.svg', width: 949, height: 80 },
};

/**
 * The Clearlab wordmark. Sourced from /public/Logo_Dual.svg so the image is
 * cached and CSP-clean (img-src 'self'). The dual treatment is used in the
 * nav; the mono treatment is reserved for the footer. Swaps to a
 * language-specific mark (see LOGO_BY_LANGUAGE) when one exists for the
 * active language.
 */
export function ClearlabWordmark({ className, alt = 'Clearlab', ...props }: Props) {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  // Match on the base subtag: a visitor whose device reports `ko-KR` (the
  // detector stores whatever navigator gives it) must get the same mark as
  // someone who picked `ko` from the dropdown.
  const logo = LOGO_BY_LANGUAGE[language.split('-')[0]] ?? DEFAULT_LOGO;

  return (
    <img
      {...props}
      src={logo.src}
      alt={alt}
      width={logo.width}
      height={logo.height}
      className={cn('h-4 w-auto', className)}
    />
  );
}
